import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Text,
  type BadgeVariant,
} from "@marekl/adminer/components";
import { dayjs } from "@marekl/adminer/datetime";
import { formatDate, formatDateTime, formatTime } from "@marekl/adminer/format";
import {
  useMessageEvents,
  type MessageEvent,
  type MessageEventType,
} from "../../lib/queries/messages";
import styles from "./MessagesPage.module.css";
import timeline from "./DeliveryTimeline.module.css";

const EVENT_META: Record<MessageEventType, { label: string; variant: BadgeVariant }> = {
  queued: { label: "Queued", variant: "info" },
  claimed: { label: "Claimed", variant: "info" },
  sent: { label: "Sent", variant: "ok" },
  attempt_failed: { label: "Attempt failed", variant: "warn" },
  lease_expired: { label: "Lease expired", variant: "warn" },
  released: { label: "Released", variant: "info" },
  failed: { label: "Failed", variant: "err" },
  retried: { label: "Retried", variant: "info" },
  redelivered: { label: "Redelivered", variant: "info" },
  suppressed: { label: "Suppressed", variant: "muted" },
  cancelled: { label: "Cancelled", variant: "muted" },
  unsubscribed: { label: "Unsubscribed", variant: "muted" },
  expired: { label: "Deadline passed", variant: "warn" },
};

function formatDurationMs(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

interface DeliveryTimelineProps {
  messageId: string;
  channel: string;
  /** Poll while the message can still change state. */
  live: boolean;
}

/** The message's delivery events, oldest first. */
export function DeliveryTimeline({ messageId, channel, live }: DeliveryTimelineProps) {
  const location = useLocation();
  const events = useMessageEvents(messageId, { live });

  // Polling stops as soon as the detail query sees a final state, which can
  // be before this query fetched the final event — fetch once more then.
  const { refetch } = events;
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) void refetch();
    wasLive.current = live;
  }, [live, refetch]);

  function relatedLink(id: string) {
    return (
      <Link to={`/messages/${id}${location.search}`}>
        <Text variant="mono">{id}</Text>
      </Link>
    );
  }

  function details(e: MessageEvent): ReactNode {
    const parts: [string, ReactNode][] = [];
    if (e.actor) parts.push(["actor", <Text variant="muted">{e.actor}</Text>]);
    if (e.code) {
      parts.push([
        "code",
        <Text variant="mono">{channel === "email" ? `SMTP ${e.code}` : `code ${e.code}`}</Text>,
      ]);
    }
    if (e.detail) parts.push(["detail", <span className={styles.errorText}>{e.detail}</span>]);
    if (e.remote_host) parts.push(["host", <Text variant="mono">{e.remote_host}</Text>]);
    if (e.next_attempt_at) {
      const when = e.next_attempt_at.isSame(dayjs(), "day")
        ? formatTime(e.next_attempt_at)
        : formatDateTime(e.next_attempt_at);
      parts.push(["next", `next attempt ${when}`]);
    }
    if (e.related_message_id) {
      parts.push([
        "related",
        e.type === "redelivered" ? (
          <>copy {relatedLink(e.related_message_id)}</>
        ) : (
          <>redelivery of {relatedLink(e.related_message_id)}</>
        ),
      ]);
    }
    if (parts.length === 0) return <Text variant="muted">—</Text>;
    return (
      <span className={styles.eventDetails}>
        {parts.map(([key, node], i) => (
          <Fragment key={key}>
            {i > 0 && <Text variant="muted"> · </Text>}
            {node}
          </Fragment>
        ))}
      </span>
    );
  }

  // Parcel tracking, not a grid: one line per event, oldest first, with the
  // time on the left rail and the ink of the event's own status on its node.
  const events_ = events.data ?? [];

  return (
    <section>
      {events.isLoading ? (
        <LoadingState label="Loading timeline…" />
      ) : events.error ? (
        <ErrorState
          error={events.error}
          title="Failed to load the timeline."
          onRetry={events.refetch}
        />
      ) : events_.length === 0 ? (
        <EmptyState
          title="No delivery events recorded"
          description="Messages created before event tracking have no timeline."
        />
      ) : (
        <ol className={timeline.track}>
          {events_.map((e, i) => {
            const meta = EVENT_META[e.type];
            // A run of events on one day reads as a run of times; the date is
            // printed when the day turns (and on the first row), the way a
            // tracking history does it. Without it a message retried overnight
            // showed 09:14 … 08:31 with no way to tell which day.
            const dayTurned =
              i === 0 || !e.created_at.isSame(events_[i - 1].created_at, "day");
            return (
              <li key={e.id} className={timeline.step} data-tone={meta.variant}>
                <span className={timeline.time}>
                  {dayTurned && <span className={timeline.day}>{formatDate(e.created_at)}</span>}
                  {formatTime(e.created_at)}
                </span>
                <span className={timeline.node} aria-hidden="true" />
                <span className={timeline.event}>
                  <b>{meta.label}</b>
                  {e.attempt > 0 && (
                    <span className={timeline.attempt}>attempt {e.attempt}</span>
                  )}
                  <span className={timeline.details}>{details(e)}</span>
                </span>
                {typeof e.duration_ms === "number" && (
                  <span className={timeline.stamp}>{formatDurationMs(e.duration_ms)}</span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
